import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { ONBOARDING_SETTINGS_CLASS } from '@pkg/components/OnboardingTutorial/styles';
import { useAppDispatch, useAppSelector, useAuthentication, useLogout } from '@pkg/hooks';

import config from '@app/config';
import { setTwitterConnectIsOpen } from '@app/store/actions/dashboard';
import { useWalletStore } from '@app/store/zustand';

import { NavButton } from '../../NavButton';
import { Toggle } from '../../Toggle';
import { collapseTransition, groupAnimation, itemAnimation } from '../../animations';
import { Buttons, Collapse, Collapseable } from '../../styles';

const { privacyLink, supportLink, termsLink } = config;

interface SectionProps {
    isOpen: boolean;
    isStatic?: boolean;
    setIsOpen?: (isOpen: boolean) => void;
}

export const SettingsSection = ({ isOpen, setIsOpen, isStatic }: SectionProps) => {
    const dispatch = useAppDispatch();
    const [translate] = useTranslation('common');

    const { toggleShowOverview } = useWalletStore();
    const { protectedAction } = useAuthentication();

    const hasTwitterData = useAppSelector((rootState) => rootState.user.userClass?.user?.twitter_data);
    const { enableTwitterConnectFlow } = useAppSelector((state) => state.featureFlags.features);

    const { logout } = useLogout();

    const handleSignOut = useCallback(() => logout(), [logout]);
    const handleToggleTwitter = useCallback(() => {
        dispatch(setTwitterConnectIsOpen(true));
    }, [dispatch]);

    // const handleToggleEmailPreferences = useCallback(() => {
    //     dispatch(setEmailPreferencesIsOpen(true));
    // }, [dispatch]);

    function handleOpenWalletManager() {
        protectedAction(() => {
            toggleShowOverview(true);
        });
    }

    return (
        <>
            <Collapseable className={ONBOARDING_SETTINGS_CLASS}>
                <Toggle
                    icon="settings"
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    isStatic={isStatic}
                    aria-expanded={isOpen}
                    aria-controls="sidenav-section-settings"
                >
                    Settings
                </Toggle>

                <Collapse
                    variants={groupAnimation}
                    initial={false}
                    animate={isOpen ? 'open' : 'closed'}
                    transition={collapseTransition}
                    aria-labelledby="sidenav-section-settings"
                >
                    <Buttons>
                        {enableTwitterConnectFlow && (
                            <NavButton onClick={handleToggleTwitter} variants={itemAnimation}>
                                {hasTwitterData ? translate('twitter_manage') : translate('twitter_connect')}
                            </NavButton>
                        )}

                        <NavButton onClick={handleOpenWalletManager} variants={itemAnimation}>
                            Connect Wallet
                        </NavButton>

                        {/*}
                        <NavButton onClick={handleToggleEmailPreferences} variants={itemAnimation}>
                            {translate('email_preferences')}
                        </NavButton>

                            <NavButton to="#" variants={itemAnimation}>
                                Pay with Yats
                            </NavButton>
                        {*/}

                        <NavButton to="/security-settings" variants={itemAnimation}>
                            2-step Verification
                        </NavButton>

                        <NavButton to="/change-password" variants={itemAnimation}>
                            Change Password
                        </NavButton>

                        <NavButton to={supportLink} newTab variants={itemAnimation}>
                            Help Center
                        </NavButton>

                        <NavButton to={termsLink} newTab variants={itemAnimation}>
                            Terms of Service
                        </NavButton>

                        <NavButton to={privacyLink} newTab variants={itemAnimation}>
                            Privacy Policy
                        </NavButton>

                        <NavButton onClick={handleSignOut} variants={itemAnimation}>
                            Sign out
                        </NavButton>
                    </Buttons>
                </Collapse>
            </Collapseable>
        </>
    );
};
